import * as React from 'react';
import moment from 'moment-timezone';

import { Action, Dispatch } from 'redux';
import { IColumn, IconButton, Link, Spinner, SpinnerSize, TextField, TooltipHost } from '@fluentui/react';
import { deleteOnSiteEvent, sendOnsiteEmail, updateOnSiteEvent } from '../../redux/on-site-manager/on-sites/actions';
import { onPageDidMount, onPageWillUnmount } from '../../redux/core';
import { setOnSiteEventActiveKey, setOnSiteEventsFilterText, setOnSiteManagerActiveKey, setPartnerFilterText } from '../../redux/ui/actions';

import { AsyncData } from '../../redux/core';
import { Clipboard } from '../core/Clipboard';
import { DateSelector } from '../core/DateSelector';
import { OkCancelDialog } from '../core/OkCancelDialog';
import { OnSite } from '../../redux/on-site-manager/on-sites/types';
import { OnSiteCreateOrEditPanel } from './OnSiteCreateOrEditPanel';
import { OnSiteDetailsList } from './OnSiteDetailsList';
import { Partner } from '../../redux/on-site-manager/partners/types';
import { ProviderData } from '../../api/providers.types';
import { ROUTES } from '../../config/routes';
import { RootState } from '../../redux/types';
import { ToastParams } from '../../redux/message/types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { fetchPartners } from '../../redux/on-site-manager/partners/actions';
import { getZeroTimeDate } from '../../util/Date';
import { toast } from '../../redux/message/actions';
import TrieSearch from 'trie-search';

export interface StateProps {
  onSiteEventActiveItem: string;
  error?: string;
  onSites: AsyncData<OnSite[]>;
  onSiteEventsFilterText: string;
  allProviders: AsyncData<ProviderData[]>;
  partners: AsyncData<Partner[]>;
}

export interface DispatchProps {
  actions: {
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
    setOnSiteEventsFilterText: (search: string) => void;
    fetchPartners: () => void;
    setOnSiteManagerActiveKey: (key: string) => void;
    setOnSiteEventActiveKey: (key: string) => void;
    updateOnSiteEvent: (onsite: OnSite) => void;
    deleteOnSiteEvent: (onsiteId: string) => void;
    setPartnerFilterText: (key: string) => void;
    sendOnsiteEmail: (onsiteId: string, isPromo: boolean, isCashPay: boolean) => void;
    toast: (params: ToastParams) => void;
  };
}

type OnSitesProps = StateProps & DispatchProps;

export interface OnSitesState {
  items: OnSite[];
  columns: IColumn[];
  eventName: string;
  onsiteId: string;
  partners: Partner[];
  editOnSite?: OnSite;
  date: string;
}

export class Component extends React.Component<OnSitesProps, OnSitesState> {
  private trie: any;

  constructor(props: OnSitesProps) {
    super(props);
    this.state = {
      items: [],
      columns: this.buildColumns(),
      eventName: '',
      onsiteId: '',
      partners: [],
      date: '',
    };
  }

  public componentDidMount() {
    this.props.actions.fetchPartners();
    this.props.actions.onPageDidMount('OnSites');
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('OnSites');
  }

  public componentDidUpdate(prevProps: OnSitesProps) {
    if (this.props.onSites !== prevProps.onSites && this.props.onSites.data) {
      this.trie = new TrieSearch(['name', 'partnerName', 'leadDoctorName', 'streetAddress', 'serviceName']);
      this.trie.addAll(this.props.onSites.data.slice());
      this.updateItems(this.props);
    }
    if (this.props.onSiteEventsFilterText !== prevProps.onSiteEventsFilterText) {
      this.updateItems(this.props);
    }
    if (!this.props.partners.isFetching && this.props.partners !== prevProps.partners && this.props.partners.data) {
      this.setState({ partners: this.props.partners.data });
    }
  }

  public render(): JSX.Element {
    return (
      <div>
        <div className="flex">
          <div style={{ marginRight: 10 }}>
            <DateSelector displayFormat="Fro\m: MM/DD/YY" defaultDate={this.state.date || new Date()} onChange={(date) => this.updateItems(this.props, date)} />
          </div>
          <div className="flex" style={{ marginLeft: 'auto', marginRight: 10, height: 32 }} />
          <div style={{ marginRight: 10 }}>{this.renderFilterControl()}</div>
          {this.renderResultsCount()}
        </div>
        {this.renderOnsiteDetailsList()}
        {this.state.editOnSite && (
          <OnSiteCreateOrEditPanel
            onSite={this.state.editOnSite}
            partner={this.state.partners.find((p) => p.id === this.state.editOnSite!.partnerId)!}
            onClose={this.onOnSiteUpdated}
          />
        )}
      </div>
    );
  }

  private updateItems(props: OnSitesProps, date?: string) {
    if (date) {
      this.setState({ date });
    }

    const start = getZeroTimeDate(date || this.state.date);
    if (props.onSiteEventsFilterText) {
      this.setState({
        items: this.trie.get(props.onSiteEventsFilterText).filter((o: any) => moment(o.startTime).isAfter(moment(start))),
      });
    } else {
      this.setState({
        items: props.onSites.data ? props.onSites.data.filter((o) => moment(o.startDateTime).isAfter(moment(start))) : [],
      });
    }
  }

  private renderOnsiteDetailsList(): JSX.Element {
    if (!this.props.partners.isFetching && !this.props.allProviders.isFetching) {
      return (
        <OnSiteDetailsList
          items={this.state.items}
          columns={this.state.columns}
          onSiteEventActiveItem={this.props.onSiteEventActiveItem}
          setOnSiteEventActiveKey={this.props.actions.setOnSiteEventActiveKey}
        />
      );
    }
    return <Spinner size={SpinnerSize.large} />;
  }

  private renderFilterControl(): JSX.Element | null {
    if (!this.props.onSites.data || this.props.onSites.data?.length === 0) {
      return null;
    }
    return <TextField iconProps={{ iconName: 'Filter' }} placeholder="Filter on-sites" value={this.props.onSiteEventsFilterText} onChange={this.onSearchChange} />;
  }

  private renderResultsCount(): JSX.Element | null {
    if (!this.props.onSites.data || this.props.onSites.data?.length === 0) {
      return null;
    }
    return <span style={{ fontSize: 20 }}> Results: {this.state.items.length}</span>;
  }

  private onOnSiteUpdated = (onSite?: OnSite) => {
    this.setState({ editOnSite: undefined });
    if (onSite) {
      this.props.actions.toast({
        type: 'success',
        title: 'Updated',
        content: 'The on-site was successfully updated.',
      });
      this.props.actions.updateOnSiteEvent(onSite);
    }
  };

  private onSearchChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    this.props.actions.setOnSiteEventsFilterText(newValue!);
  };

  private buildColumns(): IColumn[] {
    return [
      {
        key: 'partnerName',
        name: 'Partner',
        fieldName: '',
        minWidth: 80,
        maxWidth: 140,
        isResizable: true,
        onRender: (item: OnSite) => {
          const url = ROUTES.onSiteManagerEditPartner.replace(':id', item.partnerId);
          return <Link href={url}>{item.partnerName}</Link>;
        },
      },
      {
        key: 'name',
        name: 'On-Site',
        fieldName: 'name',
        minWidth: 100,
        maxWidth: 150,
        isMultiline: true,
        isResizable: true,
      },
      {
        key: 'date',
        name: 'Date',
        fieldName: '',
        minWidth: 80,
        maxWidth: 80,
        onRender: (item: OnSite) => {
          return <div>{moment(item.startDateTime).format('M/D/YYYY')}</div>;
        },
      },
      {
        key: 'time',
        name: 'Time',
        fieldName: '',
        minWidth: 110,
        maxWidth: 110,
        onRender: (item: OnSite) => {
          return (
            <div>
              {moment(item.startDateTime).format('h:mm A')} - {moment(item.endDateTime).format('h:mm A')}
            </div>
          );
        },
      },
      {
        key: 'breakTime',
        name: 'Break Time',
        fieldName: '',
        minWidth: 110,
        maxWidth: 110,
        onRender: (item: OnSite) => {
          return <div>{item.break ? moment(item.break.startTime, 'HH:mm').format('h:mm A') + ' - ' + moment(item.break.endTime, 'HH:mm').format('h:mm A') : 'No Break'}</div>;
        },
      },
      {
        key: 'serviceName',
        name: 'Service',
        fieldName: 'serviceName',
        minWidth: 100,
        maxWidth: 100,
      },
      {
        key: 'streetAddress',
        name: 'Campus',
        fieldName: '',
        minWidth: 100,
        maxWidth: 200,
        isMultiline: true,
        isResizable: true,
        onRender: (item: OnSite) => {
          const partner = this.state.partners.find((p) => p.id === item.partnerId);
          if (partner) {
            const partnerAddress = partner.addresses.find((a) => a.id === item.partnerAddressId);
            if (partnerAddress) {
              return <div>{partnerAddress.name}</div>;
            }
          }
          return null;
        },
      },
      {
        key: 'bookingUrl',
        name: 'Book link',
        fieldName: '',
        minWidth: 60,
        maxWidth: 60,
        onRender: (item: OnSite) => {
          const partner = this.state.partners.find((p) => p.id === item.partnerId);
          if (partner) {
            const partnerAddress = partner.addresses.find((a) => a.id === item.partnerAddressId);
            if (partnerAddress) {
              return (
                <Clipboard className="cursor-copy" content={partnerAddress.bookingUrl} message="Booking link copied to clipboard">
                  <TooltipHost content={partnerAddress.bookingUrl}>
                    <i className={`ms-font-m ms-Icon ms-Icon--Copy`} />
                  </TooltipHost>
                </Clipboard>
              );
            }
          }
          return null;
        },
      },
      {
        key: 'totalAppointments',
        name: 'Capacity',
        fieldName: 'totalAppointments',
        minWidth: 50,
        maxWidth: 50,
      },
      {
        key: 'appointmentsBooked',
        name: 'Booked',
        fieldName: 'appointmentsBooked',
        minWidth: 70,
        maxWidth: 70,
        onRender: (item: OnSite) => {
          return <div>{item.appointmentsBooked}</div>;
        },
      },
      {
        key: 'appointmentsFinished',
        name: 'Completed',
        fieldName: 'appointmentsFinished',
        minWidth: 60,
        maxWidth: 60,
      },
      {
        key: 'email',
        name: 'Email',
        fieldName: '',
        minWidth: 40,
        maxWidth: 40,
        onRender: (item: OnSite) => {
          const content: JSX.Element[] = [];
          if (item.isEmailSent) {
            content.push(<div key="generic">Generic email sent</div>);
          }
          if (item.isPromoEmailSent) {
            content.push(<div key="promo">Promo email sent</div>);
          }
          if (item.isCashPayEmailSent) {
            content.push(<div key="cash">Cash pay email sent</div>);
          }
          if (content.length > 0) {
            return (
              <TooltipHost tooltipProps={{ onRenderContent: () => <div>{content}</div> }}>
                <i style={{ marginRight: 6 }} className={`ms-font-m ms-Icon ms-Icon--Accept`} />
                <span>{'' + content.length}</span>
              </TooltipHost>
            );
          }
          return null;
        },
      },
      {
        key: 'action',
        name: '',
        fieldName: '',
        minWidth: 50,
        maxWidth: 50,
        onRender: (item: OnSite) => {
          return (
            <IconButton
              className="hidable-menu-button"
              allowDisabledFocus={true}
              iconProps={{ iconName: 'ContextMenu' }}
              menuProps={{
                items: [
                  {
                    key: 'edit',
                    text: 'Edit',
                    iconProps: { iconName: 'Edit' },
                    onClick: () => {
                      this.setState({ editOnSite: item });
                    },
                  },
                  {
                    key: 'booking',
                    text: 'Send booking email',
                    iconProps: { iconName: 'Send' },
                    // disabled: item.isEmailSent,
                    onClick: () => {
                      OkCancelDialog.show({
                        title: 'Send Booking Email',
                        subText: 'This will send booking email for ' + item.name + ' to onsite@heal.com. Are you sure?',
                        onOk: () => this.props.actions.sendOnsiteEmail(item.onsiteId, false, false),
                      });
                    },
                  },
                  {
                    key: 'promo',
                    text: 'Send promo code email',
                    iconProps: { iconName: 'Send' },
                    // disabled: item.isPromoEmailSent,
                    onClick: () => {
                      OkCancelDialog.show({
                        title: 'Send Promo Code Email',
                        subText: 'This will send promo code email for ' + item.name + ' to onsite@heal.com. Are you sure?',
                        onOk: () => this.props.actions.sendOnsiteEmail(item.onsiteId, true, false),
                      });
                    },
                  },
                  {
                    key: 'cash',
                    text: 'Send cash pay email',
                    iconProps: { iconName: 'Send' },
                    // disabled: item.isCashPayEmailSent,
                    onClick: () => {
                      OkCancelDialog.show({
                        title: 'Send Cash Pay Email',
                        subText: 'This will send cash pay email for ' + item.name + ' to onsite@heal.com. Are you sure?',
                        onOk: () => this.props.actions.sendOnsiteEmail(item.onsiteId, false, true),
                      });
                    },
                  },
                  {
                    key: 'delete',
                    text: 'Delete',
                    iconProps: { iconName: 'Delete' },
                    disabled: item.appointmentsBooked !== 0,
                    onClick: () => {
                      OkCancelDialog.show({
                        title: 'Delete Onsite',
                        subText: 'This will delete ' + item.name + '. Are you sure?',
                        onOk: () => this.props.actions.deleteOnSiteEvent(item.onsiteId),
                      });
                    },
                  },
                ],
              }}
            />
          );
        },
      },
    ];
  }
}

export function mapStateToProps(state: RootState): StateProps {
  return {
    onSites: state.onSiteManager.onSites.onSites,
    onSiteEventsFilterText: state.ui.onSiteEventsFilterText,
    allProviders: state.providers.allProviders,
    partners: state.onSiteManager.partners.partners,
    error: state.onSiteManager.onSites.onSites.error,
    onSiteEventActiveItem: state.ui.onSiteEventActiveItem,
  };
}

export function mapDispatchToProps(dispatch: Dispatch<Action>): DispatchProps {
  return {
    actions: bindActionCreators(
      {
        setOnSiteEventActiveKey,
        onPageDidMount,
        onPageWillUnmount,
        setOnSiteEventsFilterText,
        deleteOnSiteEvent,
        updateOnSiteEvent,
        fetchPartners,
        setOnSiteManagerActiveKey,
        setPartnerFilterText,
        sendOnsiteEmail,
        toast,
      },
      dispatch
    ),
  };
}

export const OnSites = connect(mapStateToProps, mapDispatchToProps)(Component);
